import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Check, AlertCircle, Calendar as CalendarIcon, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helpers
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const WEEKDAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

function pad(n) { return String(n).padStart(2, "0"); }
function toISODate(d) { return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`; }
function fmtDateLong(iso) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}
function isValidEmail(e) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e); }
function isValidPhone(p) { return /^[+\d][\d\s\-]{6,20}$/.test(p); }

// Step indicator
function ProgressBar({ step }) {
  const labels = ["Growth audit", "Your details", "Pick a slot"];
  return (
    <div className="flex items-center gap-2 max-w-md mx-auto mb-8" data-testid="booking-progress">
      {labels.map((l, i) => {
        const active = i + 1 <= step;
        return (
          <React.Fragment key={i}>
            <div className={`flex items-center gap-2 text-xs ${active ? "c-red" : "c-muted"}`}>
              <div className={`w-6 h-6 flex items-center justify-center text-[11px] font-semibold ${active ? "bg-red-700/30 border border-red-600 text-white" : "border border-zinc-700 text-zinc-500"}`} style={{ borderRadius: 0 }}>
                {i + 1}
              </div>
              <span className="hidden sm:inline">{l}</span>
            </div>
            {i < 2 && <div className={`flex-1 h-px ${active && i + 1 < step ? "bg-red-700" : "bg-zinc-800"}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// Calendar component
function CalendarPicker({ settings, onPick, selectedDate }) {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const [viewMonth, setViewMonth] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));

  const maxDate = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + (settings?.rolling_window_days || 30));
    return d;
  }, [today, settings]);

  const days = useMemo(() => {
    const first = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
    const last = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0);
    const startOffset = (first.getDay() + 6) % 7; // Mon=0
    const arr = [];
    for (let i = 0; i < startOffset; i++) arr.push(null);
    for (let d = 1; d <= last.getDate(); d++) arr.push(new Date(viewMonth.getFullYear(), viewMonth.getMonth(), d));
    return arr;
  }, [viewMonth]);

  const isPickable = (d) => {
    if (!d) return false;
    if (d < today) return false;
    if (d > maxDate) return false;
    const wd = (d.getDay() + 6) % 7; // Mon=0
    if (!settings?.working_days?.includes(wd)) return false;
    if (settings?.blocked_dates?.includes(toISODate(d))) return false;
    return true;
  };

  const monthLabel = `${MONTHS[viewMonth.getMonth()]} ${viewMonth.getFullYear()}`;
  const canPrev = viewMonth.getFullYear() > today.getFullYear() || viewMonth.getMonth() > today.getMonth();

  return (
    <div className="c-card max-w-md mx-auto">
      <div className="flex items-center justify-between mb-5">
        <button onClick={() => canPrev && setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))} className={`p-3 ${canPrev ? "text-white hover:c-red" : "text-zinc-700 cursor-not-allowed"}`} aria-label="Previous month">
          <ChevronLeft size={20} />
        </button>
        <div className="text-lg" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 600 }}>{monthLabel}</div>
        <button onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))} className="p-3 text-white hover:c-red" aria-label="Next month">
          <ChevronRight size={20} />
        </button>
      </div>
      <div className="grid grid-cols-7 text-[10px] tracking-[0.16em] uppercase c-muted mb-2">
        {WEEKDAYS.map((w) => <div key={w} className="text-center py-1">{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((d, i) => {
          if (!d) return <div key={i} />;
          const iso = toISODate(d);
          const pickable = isPickable(d);
          const selected = selectedDate === iso;
          return (
            <button
              key={i}
              disabled={!pickable}
              onClick={() => pickable && onPick(iso)}
              className={`h-11 text-sm transition-colors ${
                selected
                  ? "bg-red-700 text-white"
                  : pickable
                  ? "text-white hover:bg-white/5"
                  : "text-zinc-700 cursor-not-allowed"
              }`}
              style={{ borderRadius: 0 }}
              data-testid={`cal-day-${iso}`}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Slot picker
function SlotPicker({ date, slots, loading, onPick, onBack }) {
  return (
    <div className="max-w-md mx-auto mt-6">
      <button onClick={onBack} className="text-xs c-muted hover:text-white mb-4 flex items-center gap-1 py-2">
        <ChevronLeft size={14} /> Change date
      </button>
      <div className="text-base text-white mb-1" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 600 }}>
        {fmtDateLong(date)}
      </div>
      <div className="text-xs c-muted mb-5 flex items-center gap-1.5"><Clock size={12} className="c-red" /> All times in IST</div>
      {loading ? (
        <div className="c-card text-center c-muted py-8 text-sm">Loading slots…</div>
      ) : slots.length === 0 ? (
        <div className="c-card text-center c-muted py-8 text-sm">No slots available. Try another date.</div>
      ) : (
        <div className="grid grid-cols-3 gap-2" data-testid="slot-grid">
          {slots.map((t) => (
            <button
              key={t}
              onClick={() => onPick(t)}
              className="py-3.5 text-sm border border-white/10 hover:border-red-600 hover:bg-red-700/10 text-white"
              style={{ borderRadius: 0 }}
              data-testid={`slot-${t}`}
            >
              {t}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Growth Audit screens ----
const AD_SPEND_OPTIONS = ["Under ₹25K", "₹25K – ₹50K", "₹50K – ₹1L", "₹1L – ₹3L", "₹3L – ₹5L", "₹5L+"];

const AUDIT_QUESTIONS = [
  {
    key: "business_type",
    title: "What best describes your business?",
    type: "single",
    options: ["Coach / Mentor", "Consultant", "Agency", "Professional Service Business", "Course / Training Business", "Local Business", "Other"],
  },
  {
    key: "customer_source",
    title: "Where are most of your customers coming from today?",
    type: "single",
    options: ["Referrals / Word of mouth", "Organic social media", "Cold outreach / DMs", "Paid ads", "Google / Search", "Partnerships / Networking", "Multiple channels", "I don't have a predictable source yet"],
  },
  {
    key: "ad_spend",
    title: "Roughly how much are you currently investing in paid ads each month?",
    type: "single",
    options: AD_SPEND_OPTIONS,
    condition: (a) => a.customer_source === "Paid ads",
  },
  {
    key: "growth_blocker",
    title: "What's the #1 thing stopping you from growing faster right now?",
    type: "single",
    options: [
      "I'm not clear about my niche / target audience",
      "I'm not getting enough qualified leads",
      "I'm getting leads, but not enough sales",
      "My ads / funnel aren't performing",
      "I'm struggling to stand out from competitors",
      "I'm getting customers, but growth isn't predictable",
      "I'm not sure what's actually going wrong",
    ],
  },
  {
    key: "online_presence",
    title: "Where can we currently find your business online?",
    subtitle: "Select everything you currently have.",
    type: "multi",
    options: ["Website", "Instagram", "LinkedIn", "Facebook", "Google Business Profile", "YouTube", "None of these yet"],
  },
  {
    key: "prospect_outcome",
    title: "When a genuinely interested prospect contacts you, what usually happens?",
    type: "single",
    options: [
      "They usually become a customer",
      "Some buy, some don't",
      'Most say "I\'ll think about it"',
      "Price becomes the main objection",
      "They stop responding after the initial conversation",
      "I don't get enough qualified enquiries to know yet",
    ],
  },
  {
    key: "six_month_goal",
    title: "What's the biggest change you'd like to see in your business over the next 6 months?",
    type: "single",
    options: [
      "Build a predictable flow of customers",
      "Increase monthly revenue significantly",
      "Attract better-quality / higher-paying customers",
      "Make marketing and ads consistently profitable",
      "Improve sales conversions",
      "Build systems so growth doesn't depend entirely on me",
      "Scale what is already working",
    ],
  },
  {
    key: "business_stage",
    title: "Finally, where is your business right now?",
    subtitle: "This helps us recommend a strategy appropriate for your current stage.",
    type: "single",
    options: ["Just starting / validating", "Below ₹1L/month", "₹1L – ₹3L/month", "₹3L – ₹5L/month", "₹5L – ₹10L/month", "₹10L – ₹25L/month", "₹25L+/month"],
  },
];

const DIAGNOSES = {
  Positioning: "Your positioning isn't sharp enough yet — prospects can't instantly see why you're the obvious choice. Tightening your niche, promise and proof will make every channel convert better.",
  Acquisition: "Your biggest gap is a predictable customer-acquisition engine. Lead flow currently depends on effort and luck — a systemised content + funnel machine would make qualified enquiries consistent.",
  Sales: "You're generating interest but leaking conversions at the finish line. Sharpening your offer, sales conversations and follow-up will convert more of the leads you already have.",
  Scale: "You have traction — what's missing is a repeatable system that scales without you. Productising your funnel and systemising delivery will unlock the next stage of growth.",
};

function computeDiagnosis(answers) {
  const map = {
    "I'm not clear about my niche / target audience": "Positioning",
    "I'm not getting enough qualified leads": "Acquisition",
    "I'm getting leads, but not enough sales": "Sales",
    "My ads / funnel aren't performing": "Acquisition",
    "I'm struggling to stand out from competitors": "Positioning",
    "I'm getting customers, but growth isn't predictable": "Scale",
  };
  let category = map[answers.growth_blocker];
  if (!category) {
    // "I'm not sure what's actually going wrong" → infer from customer source
    category = answers.customer_source === "I don't have a predictable source yet" ? "Acquisition" : "Scale";
  }
  return { category, text: DIAGNOSES[category] };
}

function OptionCard({ selected, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-3 border px-4 py-4 text-left text-sm md:text-base transition ${selected ? "border-red-600 bg-red-700/10 text-white" : "border-white/10 text-zinc-300 hover:border-white/30"}`}
      style={{ borderRadius: 0 }}
      data-testid={testId}
    >
      <span className={`w-5 h-5 shrink-0 flex items-center justify-center border ${selected ? "bg-red-700 border-red-600" : "border-zinc-600"}`} style={{ borderRadius: 0 }}>
        {selected && <Check size={12} className="text-white" />}
      </span>
      {children}
    </button>
  );
}

// MAIN PAGE
export default function BookingPage() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("audit"); // audit -> result -> details -> schedule
  const [settings, setSettings] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedTime, setSelectedTime] = useState(null);

  const [name, setName] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [email, setEmail] = useState("");
  const [city, setCity] = useState("");
  const [hasWebsite, setHasWebsite] = useState(null); // null | true | false
  const [website, setWebsite] = useState("");
  const [bestSocial, setBestSocial] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [adBudget, setAdBudget] = useState(null); // null | "yes" | "no"
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [started, setStarted] = useState(false);
  const [qIndex, setQIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const visibleQuestions = useMemo(
    () => AUDIT_QUESTIONS.filter((q) => !q.condition || q.condition(answers)),
    [answers]
  );

  useEffect(() => {
    document.title = "Get Your Business Growth Audit — BrandChilly";
    axios.get(`${API}/booking/settings`).then((r) => setSettings(r.data)).catch(() => setError("Could not load booking settings"));
    try {
      if (window.fbq) window.fbq("track", "InitiateCheckout", { content_name: "GrowthAudit", content_category: "Strategy Call Booking" });
      if (window.gtag) window.gtag("event", "begin_checkout", { content_name: "GrowthAudit" });
    } catch (e) { /* swallow */ }
  }, []);

  useEffect(() => {
    if (!selectedDate) return;
    setSlotsLoading(true);
    axios.get(`${API}/booking/slots?date=${selectedDate}`)
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [selectedDate]);

  const onAnswer = (key, val) => setAnswers((a) => ({ ...a, [key]: val }));

  const startAudit = () => {
    if (!name.trim() || !isValidPhone(whatsapp) || !isValidEmail(email)) return;
    setStarted(true);
  };

  const submitBooking = async () => {
    setSubmitting(true);
    setError("");
    const diag = computeDiagnosis(answers);
    let engagement = {};
    try {
      const e = JSON.parse(sessionStorage.getItem("bc_fc_engagement") || "null");
      if (e) engagement = { time_on_page: e.time_on_page, scroll_percent: e.scroll_percent, video_watch_percent: e.video_watch_percent };
    } catch (err) { /* swallow */ }
    try {
      const payload = {
        date: selectedDate,
        time: selectedTime,
        name: name.trim(),
        whatsapp: whatsapp.trim(),
        email: email.trim(),
        city: city.trim() || null,
        ...engagement,
        business_type: answers.business_type || null,
        customer_source: answers.customer_source || null,
        growth_blocker: answers.growth_blocker || null,
        online_presence: answers.online_presence || null,
        prospect_outcome: answers.prospect_outcome || null,
        ad_spend: answers.ad_spend || null,
        six_month_goal: answers.six_month_goal || null,
        business_stage: answers.business_stage || null,
        diagnosis: `${diag.category} — ${diag.text}`,
        website: hasWebsite === true ? website.trim() : null,
        best_social: bestSocial.trim() || null,
        founder_linkedin: linkedin.trim() || null,
        source: sessionStorage.getItem("bc_booking_source") || null,
      };
      const { data } = await axios.post(`${API}/booking`, payload);
      sessionStorage.removeItem("bc_booking_source");
      sessionStorage.removeItem("bc_fc_engagement");
      navigate("/confirmed", { state: { booking: data } });
    } catch (err) {
      const code = err?.response?.status;
      if (code === 409) {
        setError("This slot was just taken. Please pick another time.");
        setSelectedTime(null);
      } else {
        setError(err?.response?.data?.detail || "Could not complete booking. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const step = phase === "audit" ? 1 : phase === "details" ? 2 : 3;
  const diag = useMemo(() => computeDiagnosis(answers), [answers]);
  const q = visibleQuestions[qIndex];
  const isLast = qIndex === visibleQuestions.length - 1;
  const qValue = q ? answers[q.key] : null;
  const qValid = q ? (q.type === "multi" ? (qValue || []).length > 0 : Boolean(qValue)) : false;

  const goNext = () => {
    if (!qValid) return;
    if (isLast) setPhase("details");
    else setQIndex(qIndex + 1);
  };

  return (
    <div className="coaches-page" style={{ minHeight: "100vh", background: "var(--c-bg)" }}>
      {/* Header — prominent logo for brand recall across every screen */}
      <header className="sticky top-0 z-40 bg-black/90 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-20 md:h-24 flex items-center justify-between">
          <a href="/" className="flex items-center" data-testid="booking-logo">
            <img src="/assets/logo_header.png" alt="BrandChilly" className="h-11 md:h-14 w-auto" />
          </a>
          <div className="text-[10px] md:text-[11px] c-muted tracking-[0.18em] uppercase">
            All times in IST
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 md:px-8 py-8 md:py-16">
        <ProgressBar step={step} />

        {/* PHASE 1 — AUDIT */}
        {phase === "audit" && !started && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="max-w-xl mx-auto" data-testid="audit-intro">
            <div className="c-eyebrow mb-3">Business Growth Audit</div>
            <h1 className="text-3xl md:text-5xl leading-[1.05] mb-4">Verify Your Eligibility</h1>
            <p className="c-muted text-sm md:text-base mb-8">
              Please share the details of your business which will help us to understand it better — and we will be able to check if we both are a good fit for each other or not.
            </p>

            <label className="block text-xs c-muted mb-1.5">Full Name</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name" className="w-full bg-transparent border border-white/10 text-white px-4 py-3.5 text-base focus:border-red-600 outline-none mb-4" style={{ borderRadius: 0 }} data-testid="audit-name" />

            <label className="block text-xs c-muted mb-1.5">WhatsApp Number</label>
            <input type="tel" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="+91 98765 43210" className="w-full bg-transparent border border-white/10 text-white px-4 py-3.5 text-base focus:border-red-600 outline-none mb-4" style={{ borderRadius: 0 }} data-testid="audit-whatsapp" />

            <label className="block text-xs c-muted mb-1.5">Email Address</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@yourbrand.com" className="w-full bg-transparent border border-white/10 text-white px-4 py-3.5 text-base focus:border-red-600 outline-none mb-6" style={{ borderRadius: 0 }} data-testid="audit-email" />

            <button
              onClick={startAudit}
              disabled={!name.trim() || !isValidPhone(whatsapp) || !isValidEmail(email)}
              className="c-btn-primary w-full justify-center text-sm py-4 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="audit-start-btn"
            >
              Check My Eligibility <ChevronRight size={16} />
            </button>
            <p className="text-[11px] c-muted mt-4 text-center">Takes about 60 seconds. Your answers help us assess fit.</p>
          </motion.div>
        )}

        {phase === "audit" && started && q && (
          <div>
            <div className="text-center mb-7">
              <div className="c-eyebrow mb-2">Question {qIndex + 1} of {visibleQuestions.length}</div>
              <div className="max-w-xs mx-auto h-0.5 bg-zinc-800 overflow-hidden">
                <div className="h-full bg-red-600 transition-all duration-500" style={{ width: `${((qIndex + 1) / visibleQuestions.length) * 100}%` }} />
              </div>
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={q.key}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="max-w-xl mx-auto"
              >
                <h2 className="text-2xl md:text-4xl mb-3 leading-[1.15]">{q.title}</h2>
                {q.subtitle && <p className="c-muted text-sm mb-5">{q.subtitle}</p>}
                {!q.subtitle && <div className="mb-4" />}

                <div className="space-y-2" data-testid={`q-${q.key}`}>
                  {q.options.map((o, i) => {
                    const selected = q.type === "multi" ? (qValue || []).includes(o) : qValue === o;
                    return (
                      <OptionCard
                        key={o}
                        testId={`audit-option-${q.key}-${i}`}
                        selected={selected}
                        onClick={() => {
                          if (q.type === "multi") {
                            const cur = qValue || [];
                            onAnswer(q.key, selected ? cur.filter((x) => x !== o) : [...cur, o]);
                          } else {
                            onAnswer(q.key, o);
                          }
                        }}
                      >
                        {o}
                      </OptionCard>
                    );
                  })}
                </div>

                <div className="mt-8 flex items-center justify-between gap-3">
                  {qIndex > 0 ? (
                    <button onClick={() => setQIndex(qIndex - 1)} className="text-sm c-muted hover:text-white flex items-center gap-1 py-2" data-testid="q-prev">
                      <ChevronLeft size={16} /> Previous
                    </button>
                  ) : <div />}
                  <button
                    onClick={goNext}
                    disabled={!qValid}
                    className="c-btn-primary text-sm py-3.5 px-5 flex-1 sm:flex-none justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="q-next"
                  >
                    {isLast ? "See My Growth Diagnosis" : "Next"} <ChevronRight size={16} />
                  </button>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        )}

        {/* PHASE 2 — DETAILS (website, socials, ad budget) */}
        {phase === "details" && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="max-w-xl mx-auto" data-testid="audit-details">
            <div className="c-eyebrow mb-3">Almost there</div>
            <h2 className="text-2xl md:text-4xl leading-[1.15] mb-3">Kindly fill the details and we will check if we are a good fit or not.</h2>
            <p className="c-muted text-sm mb-8">
              This helps us come prepared with specific observations about your brand.
            </p>

            <label className="block text-xs c-muted mb-1.5">Which city are you based in? *</label>
            <input
              type="text" value={city} onChange={(e) => setCity(e.target.value)}
              placeholder="e.g. Mumbai"
              className="w-full bg-transparent border border-white/10 text-white px-4 py-3.5 text-base focus:border-red-600 outline-none mb-5"
              style={{ borderRadius: 0 }}
              data-testid="audit-city"
            />

            <label className="block text-xs c-muted mb-2">Do you have a business website? *</label>
            <div className="flex gap-2 mb-5">
              <button
                onClick={() => setHasWebsite(true)}
                className={`flex-1 py-3.5 text-sm border transition-all ${hasWebsite === true ? "border-red-600 bg-red-700/10 text-white" : "border-white/10 c-muted hover:text-white"}`}
                style={{ borderRadius: 0 }}
                data-testid="audit-has-website-yes"
              >
                Yes
              </button>
              <button
                onClick={() => { setHasWebsite(false); setWebsite(""); }}
                className={`flex-1 py-3.5 text-sm border transition-all ${hasWebsite === false ? "border-red-600 bg-red-700/10 text-white" : "border-white/10 c-muted hover:text-white"}`}
                style={{ borderRadius: 0 }}
                data-testid="audit-has-website-no"
              >
                No
              </button>
            </div>

            {hasWebsite === true && (
              <>
                <label className="block text-xs c-muted mb-1.5">Please share your website link *</label>
                <input
                  type="text" value={website} onChange={(e) => setWebsite(e.target.value)}
                  placeholder="yourbrand.com"
                  className="w-full bg-transparent border border-white/10 text-white px-4 py-3.5 text-base focus:border-red-600 outline-none mb-5"
                  style={{ borderRadius: 0 }}
                  data-testid="audit-website"
                />
              </>
            )}

            <label className="block text-xs c-muted mb-1.5">Your most active social media link for business — Instagram, YouTube or LinkedIn *</label>
            <input
              type="text" value={bestSocial} onChange={(e) => setBestSocial(e.target.value)}
              placeholder="instagram.com/@yourhandle"
              className="w-full bg-transparent border border-white/10 text-white px-4 py-3.5 text-base focus:border-red-600 outline-none mb-5"
              style={{ borderRadius: 0 }}
              data-testid="audit-best-social"
            />

            <label className="block text-xs c-muted mb-1.5">Founder's LinkedIn profile *</label>
            <input
              type="text" value={linkedin} onChange={(e) => setLinkedin(e.target.value)}
              placeholder="linkedin.com/in/yourname"
              className="w-full bg-transparent border border-white/10 text-white px-4 py-3.5 text-base focus:border-red-600 outline-none mb-7"
              style={{ borderRadius: 0 }}
              data-testid="audit-founder-linkedin"
            />

            <div className="border border-white/10 p-5 mb-7" style={{ borderRadius: 0 }} data-testid="audit-budget-block">
              <p className="text-sm text-white leading-relaxed mb-1.5">
                Our system involves paid advertisements on Meta &amp; Google. This requires a budget of <span className="c-red">₹30,000–₹50,000 monthly</span> to get consistent clients.
              </p>
              <p className="text-xs c-muted mb-4">Are you ready to invest this amount? *</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setAdBudget("yes")}
                  className={`flex-1 py-3.5 text-sm border transition-all ${adBudget === "yes" ? "border-red-600 bg-red-700/10 text-white" : "border-white/10 c-muted hover:text-white"}`}
                  style={{ borderRadius: 0 }}
                  data-testid="audit-budget-yes"
                >
                  Yes
                </button>
                <button
                  onClick={() => setAdBudget("no")}
                  className={`flex-1 py-3.5 text-sm border transition-all ${adBudget === "no" ? "border-red-600 bg-red-700/10 text-white" : "border-white/10 c-muted hover:text-white"}`}
                  style={{ borderRadius: 0 }}
                  data-testid="audit-budget-no"
                >
                  No
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3">
              <button onClick={() => setPhase("audit")} className="text-sm c-muted hover:text-white flex items-center gap-1 py-2" data-testid="details-back">
                <ChevronLeft size={16} /> Back
              </button>
              <button
                onClick={async () => {
                  if (adBudget === "no") {
                    // Capture for nurture, but don't book the call
                    try {
                      axios.post(`${API}/booking/disqualified-lead`, {
                        name: name.trim(), email: email.trim(), whatsapp: whatsapp.trim(),
                        social_handles: [website, bestSocial, linkedin].filter(Boolean).join(" · "),
                        revenue_band: "ad_budget_not_ready",
                        source: "budget_not_ready",
                      });
                    } catch (e) { /* swallow */ }
                    setShowBudgetModal(true);
                    return;
                  }
                  setPhase("schedule");
                }}
                disabled={!(city.trim() && hasWebsite !== null && (hasWebsite === false || website.trim()) && bestSocial.trim() && linkedin.trim() && adBudget !== null)}
                className="c-btn-primary text-sm py-3.5 px-5 flex-1 sm:flex-none justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="details-continue-btn"
              >
                Continue to Slots <ChevronRight size={16} />
              </button>
            </div>
          </motion.div>
        )}

        {/* BUDGET NOT READY — DISQUALIFICATION MODAL */}
        {showBudgetModal && (
          <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="budget-modal">
            <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }} className="c-card max-w-md w-full relative text-center">
              <div className="c-eyebrow mb-4">We Appreciate Your Transparency!</div>
              <p className="text-sm c-muted leading-relaxed mb-5">
                To deliver real ROI, our paid traffic systems need a dedicated monthly ad budget. Because you're not looking to deploy that right now, booking a discovery call won't be the best use of your valuable time.
              </p>
              <p className="text-sm text-white leading-relaxed mb-4">
                But don't worry — we regularly share actionable strategies, case studies, and insights you can use to grow without heavy ad spend:
              </p>
              <div className="flex flex-col sm:flex-row gap-2 justify-center mb-5">
                <a href="https://instagram.com/saurabh.khaspuri" target="_blank" rel="noopener noreferrer" className="c-btn-primary text-xs py-3 px-4 justify-center" data-testid="budget-modal-instagram">
                  Follow on Instagram
                </a>
                <a href="https://youtube.com/@brandchilly" target="_blank" rel="noopener noreferrer" className="c-btn-primary text-xs py-3 px-4 justify-center" data-testid="budget-modal-youtube">
                  Subscribe on YouTube
                </a>
              </div>
              <p className="text-xs c-muted mb-6">Check them out, and let's connect again when you're ready to scale!</p>
              <button onClick={() => setShowBudgetModal(false)} className="text-sm c-muted hover:text-white border border-white/10 px-6 py-2.5 transition-colors" style={{ borderRadius: 0 }} data-testid="budget-modal-close">
                Close
              </button>
            </motion.div>
          </div>
        )}

        {/* PHASE 3 — SCHEDULE */}
        {phase === "schedule" && settings && (
          <div>
            <div className="text-center max-w-xl mx-auto mb-8">
              <div className="c-eyebrow mb-3">Final Step</div>
              <h1 className="text-2xl md:text-5xl leading-[1.1]">Choose Your Strategy Call Slot</h1>
              <p className="mt-4 c-muted text-sm md:text-base">All times shown in IST (India Standard Time).</p>
            </div>

            {!selectedDate ? (
              <CalendarPicker settings={settings} selectedDate={selectedDate} onPick={(d) => setSelectedDate(d)} />
            ) : !selectedTime ? (
              <SlotPicker date={selectedDate} slots={slots} loading={slotsLoading} onBack={() => setSelectedDate(null)} onPick={(t) => setSelectedTime(t)} />
            ) : (
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="max-w-md mx-auto c-card text-center" data-testid="booking-summary">
                <div className="c-eyebrow mb-3">Confirm your slot</div>
                <div className="text-lg text-white mb-1" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 600 }}>
                  {fmtDateLong(selectedDate)}
                </div>
                <div className="text-sm c-red mb-1 flex items-center justify-center gap-1.5"><Clock size={13} /> {selectedTime} IST</div>
                <div className="text-xs c-muted mb-6 flex items-center justify-center gap-1.5"><CalendarIcon size={11} /> {name.trim()} · {email.trim()}</div>
                <button
                  onClick={submitBooking}
                  disabled={submitting}
                  className="c-btn-primary w-full justify-center text-sm py-4 disabled:opacity-40"
                  data-testid="booking-confirm-btn"
                >
                  {submitting ? "Booking…" : "Confirm My Booking"} {!submitting && <Check size={16} />}
                </button>
                <button onClick={() => setSelectedTime(null)} className="text-xs c-muted hover:text-white mt-4 underline py-2" data-testid="booking-change-slot">
                  Change slot
                </button>
              </motion.div>
            )}

            {error && (
              <div className="max-w-xl mx-auto mt-6 px-4 py-3 border border-red-700/40 bg-red-900/20 text-red-200 text-sm flex items-center gap-2" style={{ borderRadius: 0 }} data-testid="booking-error">
                <AlertCircle size={14} /> {error}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
